﻿import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, LoadingSpinner } from '../../components/ui'
import { usePermission } from '../../hooks/usePermissions'
import { qualityService } from '../../services/quality'
import { supabase } from '../../lib/supabase'
import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon, EnvelopeIcon, DocumentTextIcon, ArrowDownTrayIcon, PaperClipIcon } from '@heroicons/react/24/outline'
import {
  QualityCategory,
  QualityParameter,
  QualityTicket,
  QualityTicketAttachment,
  QualityTicketComment,
  QualityTicketHistory,
  QualityImprocedentReason,
  QualityTicketOrigin,
  QualityTicketStage,
  QualityTicketType,
  Severity,
  TicketKind,
  formatTicketNumber,
} from '../../types/quality'
import { buildDepartmentLabelMap, DEPARTMENT_RESP_LABEL, formatBrokerTicketLabel, formatRequesterDisplay, type QualityActiveProfile } from './qualidadeDisplay'
import { isQualityDepartmentUser, TICKET_KIND_BADGE_COLORS, TICKET_KIND_LABELS, descriptionLabelForKind, justificationLabelForKind } from './qualidadeKind'
import { useAuthContext } from '../../contexts/AuthContext'
import { parseQualityBusinessDayWindow, getElapsedBusinessHoursMs, formatBusinessHours, parseSlaHours, addBusinessHours, parseSlaPrimeiraRespostaGroupIds, computeMaxDeadline, isMaxDeadlineBreached, formatMaxDeadlineLabel, formatMaxDeadlineDatePtBr, MAX_DEADLINE_UNIT_LABELS } from './qualityBusinessHours'
import { formatDateOnlyPtBr } from '../../utils/dateOnly'
import {
  formatTicketAttachmentSize,
  isAllowedTicketAttachmentFile,
  isTicketAttachmentImage,
  isTicketAttachmentPdf,
  isTicketAttachmentWithinSizeLimit,
  TICKET_ATTACHMENT_ACCEPT,
  TICKET_ATTACHMENT_MAX_MB,
} from './qualidadeAttachments'

const SEVERITY_LABELS: Record<Severity, string> = {
  low: 'Baixa',
  medium: 'Média',
  high: 'Alta',
}

const SEVERITY_COLORS: Record<Severity, string> = {
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

const FIELD_LABELS: Record<string, string> = {
  stage_id: 'Etapa',
  severity: 'Prioridade',
  kind: 'Tipo de Registro',
  category_id: 'Categoria',
  department_id: DEPARTMENT_RESP_LABEL,
  requester_user_id: 'Solicitante',
  ticket_type_id: 'Motivo',
  origin_id: 'Onde',
  improcedent_reason_id: 'Registo improcedente',
  external_link: 'Link externo',
  broker_id: 'Corretor',
  policy_number: 'Apólice',
  convenio_code: 'Subgrupo',
  max_deadline_unit: 'Prazo máximo (unidade)',
  max_deadline_value: 'Prazo máximo (valor)',
  max_deadline_notes: 'Obs. do prazo',
  justification: 'Justificativa',
}

type HistoryLookupCtx = {
  stages: QualityTicketStage[]
  categories: QualityCategory[]
  departments: { id: string; name: string; display_name: string }[]
  profiles: QualityActiveProfile[]
  departmentLabelMap: Map<string, string>
  ticketTypes: QualityTicketType[]
  ticketOrigins: QualityTicketOrigin[]
  improcedentReasons: QualityImprocedentReason[]
  /** Broker do próprio ticket (para resolver label no histórico quando o UUID bate). */
  ticketBroker?: { id: string; name: string; id_scgp: number | null } | null
}

function formatHistoryValue(fieldChanged: string, value: string | null, ctx: HistoryLookupCtx): string {
  if (value == null || value === '') return '—'
  switch (fieldChanged) {
    case 'stage_id': {
      const st = ctx.stages.find(s => s.id === value)
      return st?.name ?? 'Etapa indisponível'
    }
    case 'category_id': {
      const c = ctx.categories.find(x => x.id === value)
      return c?.display_name ?? c?.name ?? 'Categoria indisponível'
    }
    case 'department_id': {
      const d = ctx.departments.find(x => x.id === value)
      return d?.display_name ?? d?.name ?? 'Departamento indisponível'
    }
    case 'requester_user_id': {
      const p = ctx.profiles.find(x => x.id === value)
      if (!p) return 'Usuário indisponível'
      return formatRequesterDisplay(p, ctx.profiles, ctx.departmentLabelMap)
    }
    case 'ticket_type_id': {
      const t = ctx.ticketTypes.find(x => x.id === value)
      return t?.display_name ?? 'Tipo indisponível'
    }
    case 'origin_id': {
      const o = ctx.ticketOrigins.find(x => x.id === value)
      return o?.display_name ?? 'Onde indisponível'
    }
    case 'improcedent_reason_id': {
      const r = ctx.improcedentReasons.find(x => x.id === value)
      return r?.display_name ?? 'Registo improcedente indisponível'
    }
    case 'broker_id': {
      if (ctx.ticketBroker && ctx.ticketBroker.id === value) {
        return formatBrokerTicketLabel(ctx.ticketBroker)
      }
      return value ? `Corretor ${value.slice(0, 8)}…` : '—'
    }
    case 'policy_number':
    case 'convenio_code':
    case 'max_deadline_value':
      return value
    case 'max_deadline_unit':
      return MAX_DEADLINE_UNIT_LABELS[value as keyof typeof MAX_DEADLINE_UNIT_LABELS] ?? value
    case 'max_deadline_notes':
    case 'justification':
      return value
    case 'severity':
      return SEVERITY_LABELS[value as Severity] ?? value
    case 'kind':
      return TICKET_KIND_LABELS[value as TicketKind] ?? value
    default:
      return value
  }
}

export interface TicketDetailDrawerProps {
  incident: QualityTicket
  stages: QualityTicketStage[]
  categories: QualityCategory[]
  departments: { id: string; name: string; display_name: string }[]
  activeProfiles: QualityActiveProfile[]
  incidentTypes: QualityTicketType[]
  incidentOrigins: QualityTicketOrigin[]
  history: QualityTicketHistory[]
  historyLoading: boolean
  onClose: () => void
  onEdit: (ticket: QualityTicket) => void
  /** Exclusão lógica do ticket (confirmação e refresh ficam no handler). */
  onDelete?: (ticketId: string) => void | Promise<void>
  /** Módulo usado na permissão do botão Editar (ex.: qualidade_tickets). */
  editPermissionModuleName: string
  /** Parâmetros de qualidade (hora_util_inicio / hora_util_fim) para cálculo de horas úteis. */
  qualityParameters?: QualityParameter[]
  /** Cadastro de registo improcedente (histórico e exibição). */
  improcedentReasons?: QualityImprocedentReason[]
}

/** @deprecated Use TicketDetailDrawerProps */
export type IncidentDetailDrawerProps = TicketDetailDrawerProps

export const TicketDetailDrawer: React.FC<TicketDetailDrawerProps> = ({
  incident,
  stages,
  categories,
  departments,
  activeProfiles,
  incidentTypes,
  incidentOrigins,
  history,
  historyLoading,
  onClose,
  onEdit,
  onDelete,
  editPermissionModuleName,
  qualityParameters = [],
  improcedentReasons = [],
}) => {
  const { profile } = useAuthContext()
  const userDepartmentId = (profile as { department_id?: string | null } | null)?.department_id ?? null
  const isQualityDeptUser = useMemo(
    () => isQualityDepartmentUser(userDepartmentId, qualityParameters),
    [userDepartmentId, qualityParameters],
  )

  const { hasPermission: canUpdate, loading: updatePermLoading } = usePermission(editPermissionModuleName, 'update')
  const { hasPermission: canDelete, loading: deletePermLoading } = usePermission(editPermissionModuleName, 'delete')
  const { hasPermission: canViewAllTickets, loading: viewValuesPermLoading } = usePermission(editPermissionModuleName, 'view_values')
  const canEditTicket = !updatePermLoading && !viewValuesPermLoading && (canUpdate || canViewAllTickets)
  const canDeleteTicket = !deletePermLoading && !viewValuesPermLoading && (canDelete || canViewAllTickets)

  const stage = stages.find(s => s.id === incident.stage_id)
  const departmentLabelMap = useMemo(() => buildDepartmentLabelMap(departments), [departments])

  const registrarDisplayName = useMemo(() => {
    const fromJoin = incident.registered_by?.name?.trim()
    if (fromJoin) return fromJoin
    const uid = incident.registered_by_user_id
    if (uid) {
      const p = activeProfiles.find(x => x.id === uid)
      if (p?.name?.trim()) return p.name.trim()
    }
    return null
  }, [incident.registered_by, incident.registered_by_user_id, activeProfiles])

  const createdAtLabel = useMemo(() => {
    if (!incident.created_at) return null
    try {
      return new Date(incident.created_at).toLocaleString('pt-BR', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    } catch {
      return null
    }
  }, [incident.created_at])

  const historyCtx: HistoryLookupCtx = {
    stages,
    categories,
    departments,
    profiles: activeProfiles,
    departmentLabelMap,
    ticketTypes: incidentTypes,
    ticketOrigins: incidentOrigins,
    improcedentReasons,
    ticketBroker: incident.broker ?? null,
  }

  const [comments, setComments] = useState<QualityTicketComment[]>([])
  const [commentsLoading, setCommentsLoading] = useState(true)

  const { businessHoursLabel, slaDeadlineLabel, slaBreached, firstResponseLabel } = useMemo(() => {
    const { dayStartHour, dayEndHour } = parseQualityBusinessDayWindow(qualityParameters)
    const start = new Date(incident.created_at)

    // Primeiro comentário qualificado para SLA de 1ª resposta (menor created_at, sem excluídos).
    // Se sla_grupo_usuario_primeira_resposta tiver grupos, considera comentários de autores
    // em qualquer um deles; lista vazia aceita qualquer autor (legado).
    const slaGroupIds = parseSlaPrimeiraRespostaGroupIds(qualityParameters)
    const slaGroupSet = new Set(slaGroupIds)
    const activeComments = comments.filter(c => !c.deleted_at)
    const slaEligibleComments = slaGroupSet.size > 0
      ? activeComments.filter(c => c.author?.user_group_id != null && slaGroupSet.has(c.author.user_group_id))
      : activeComments
    const firstComment = slaEligibleComments.length > 0
      ? slaEligibleComments.reduce((a, b) =>
          new Date(a.created_at) <= new Date(b.created_at) ? a : b
        )
      : null
    const firstCommentDate = firstComment ? new Date(firstComment.created_at) : null

    // Tempo decorrido em horas úteis: created_at → 1º comentário (ou agora)
    const end = firstCommentDate ?? new Date()
    const ms  = getElapsedBusinessHoursMs(start, end, dayStartHour, dayEndHour)

    const fmt = (d: Date) =>
      d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })

    const slaHours = parseSlaHours(qualityParameters, incident.kind)
    const deadline = slaHours != null
      ? addBusinessHours(start, slaHours, dayStartHour, dayEndHour)
      : null

    // SLA vencido: prazo existe E (1º comentário veio após prazo OU ainda sem resposta e agora > prazo)
    const slaBreached = deadline != null && (
      firstCommentDate
        ? firstCommentDate > deadline
        : new Date() > deadline
    )

    return {
      businessHoursLabel: formatBusinessHours(ms),
      slaDeadlineLabel:   deadline ? fmt(deadline) : null,
      slaBreached,
      firstResponseLabel: firstCommentDate ? fmt(firstCommentDate) : null,
    }
  }, [qualityParameters, incident.created_at, incident.kind, comments])

  const maxDeadlineInfo = useMemo(() => {
    const unit = incident.max_deadline_unit
    const value = incident.max_deadline_value
    if (!unit || !value || value <= 0) {
      return { configured: false as const }
    }
    const deadline = computeMaxDeadline(incident.created_at, unit, value)
    const breached = isMaxDeadlineBreached(deadline, incident.closed_at)
    return {
      configured: true as const,
      label: formatMaxDeadlineLabel(unit, value),
      deadlineLabel: formatMaxDeadlineDatePtBr(deadline),
      breached,
      notes: incident.max_deadline_notes?.trim() || null,
    }
  }, [
    incident.created_at,
    incident.closed_at,
    incident.max_deadline_unit,
    incident.max_deadline_value,
    incident.max_deadline_notes,
  ])

  const [newComment, setNewComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const commentFileInputRef = useRef<HTMLInputElement>(null)
  const [pendingCommentFiles, setPendingCommentFiles] = useState<File[]>([])
  const [pendingCommentPreviews, setPendingCommentPreviews] = useState<(string | null)[]>([])
  const [commentAttachmentError, setCommentAttachmentError] = useState<string | null>(null)

  // Anexos / imagens
  const [attachments, setAttachments] = useState<QualityTicketAttachment[]>([])
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({})
  const [attachmentsLoading, setAttachmentsLoading] = useState(true)
  // Lightbox (somente imagens)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  const imageAttachments = useMemo(() => {
    const commentImages = comments.flatMap(c =>
      (c.attachments ?? []).filter(att => isTicketAttachmentImage(att))
    )
    return [
      ...attachments.filter(att => isTicketAttachmentImage(att)),
      ...commentImages,
    ]
  }, [attachments, comments])

  const groupAttachmentsByCommentId = (commentAtts: QualityTicketAttachment[]) => {
    const byComment: Record<string, QualityTicketAttachment[]> = {}
    for (const att of commentAtts) {
      if (!att.comment_id) continue
      byComment[att.comment_id] = byComment[att.comment_id] ?? []
      byComment[att.comment_id].push(att)
    }
    return byComment
  }

  const loadSignedUrls = async (atts: QualityTicketAttachment[]) => {
    const urls: Record<string, string> = {}
    await Promise.all(
      atts.map(async att => {
        try {
          urls[att.id] = await qualityService.getAttachmentSignedUrl(att.file_path)
        } catch { /* ignora */ }
      })
    )
    return urls
  }

  useEffect(() => {
    setCommentsLoading(true)
    setAttachmentsLoading(true)
    setPendingCommentFiles([])
    setPendingCommentPreviews([])
    setCommentAttachmentError(null)
    Promise.all([
      qualityService.listComments(incident.id),
      supabase.auth.getUser(),
      qualityService.listTicketAttachments(incident.id),
      qualityService.listCommentAttachmentsByTicketId(incident.id),
    ]).then(async ([list, { data: { user } }, atts, commentAtts]) => {
      const byComment = groupAttachmentsByCommentId(commentAtts)
      setComments(list.map(c => ({ ...c, attachments: byComment[c.id] ?? [] })))
      setCurrentUserId(user?.id ?? null)
      setCommentsLoading(false)

      setAttachments(atts)
      const urls = await loadSignedUrls([...atts, ...commentAtts])
      setAttachmentUrls(urls)
      setAttachmentsLoading(false)
    })
  }, [incident.id])

  useEffect(() => {
    return () => { pendingCommentPreviews.forEach(url => { if (url) URL.revokeObjectURL(url) }) }
  }, [pendingCommentPreviews])

  const handleCommentFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    const valid: File[] = []
    const invalid: string[] = []
    const tooLarge: string[] = []
    for (const file of files) {
      if (!isAllowedTicketAttachmentFile(file)) {
        invalid.push(file.name)
        continue
      }
      if (!isTicketAttachmentWithinSizeLimit(file)) {
        tooLarge.push(file.name)
        continue
      }
      valid.push(file)
    }

    const errors: string[] = []
    if (invalid.length) {
      errors.push(`Formato não permitido: ${invalid.join(', ')}. Use imagens, PDF ou e-mails (.msg, .eml).`)
    }
    if (tooLarge.length) {
      errors.push(`Arquivo(s) acima de ${TICKET_ATTACHMENT_MAX_MB} MB: ${tooLarge.join(', ')}.`)
    }
    setCommentAttachmentError(errors.length ? errors.join(' ') : null)

    if (!valid.length) {
      e.target.value = ''
      return
    }

    const newPreviews = valid.map(f =>
      f.type.startsWith('image/') || isTicketAttachmentImage({ file_name: f.name, mime_type: f.type })
        ? URL.createObjectURL(f)
        : null
    )
    setPendingCommentFiles(prev => [...prev, ...valid])
    setPendingCommentPreviews(prev => [...prev, ...newPreviews])
    e.target.value = ''
  }, [])

  const removePendingCommentFile = (index: number) => {
    const preview = pendingCommentPreviews[index]
    if (preview) URL.revokeObjectURL(preview)
    setPendingCommentFiles(prev => prev.filter((_, i) => i !== index))
    setPendingCommentPreviews(prev => prev.filter((_, i) => i !== index))
  }

  const clearPendingCommentFiles = () => {
    pendingCommentPreviews.forEach(url => { if (url) URL.revokeObjectURL(url) })
    setPendingCommentFiles([])
    setPendingCommentPreviews([])
    setCommentAttachmentError(null)
  }

  const openLightboxForAttachment = (att: QualityTicketAttachment) => {
    const idx = imageAttachments.findIndex(a => a.id === att.id)
    if (idx >= 0) setLightboxIndex(idx)
  }

  const submitComment = async () => {
    const content = newComment.trim()
    if (!content) return
    const filesToUpload = [...pendingCommentFiles]
    setSubmitting(true)
    try {
      const added = await qualityService.addComment(incident.id, content)
      const uploaded: QualityTicketAttachment[] = []
      const failed: string[] = []

      if (filesToUpload.length > 0) {
        await Promise.all(
          filesToUpload.map(async file => {
            try {
              const att = await qualityService.uploadTicketAttachment(incident.id, file, added.id)
              if (att) uploaded.push(att)
            } catch {
              failed.push(file.name)
            }
          })
        )
      }

      if (uploaded.length > 0) {
        const urls = await loadSignedUrls(uploaded)
        setAttachmentUrls(prev => ({ ...prev, ...urls }))
      }

      setComments(prev => [...prev, { ...added, attachments: uploaded }])
      setNewComment('')
      clearPendingCommentFiles()
      textareaRef.current?.focus()

      if (failed.length > 0) {
        window.alert(
          `Comentário enviado, mas não foi possível anexar: ${failed.join(', ')}.`
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  const removeComment = async (id: string) => {
    if (!window.confirm('Excluir este comentário?')) return
    await qualityService.deleteComment(id)
    setComments(prev => prev.filter(c => c.id !== id))
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-full sm:w-[793.5px] bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div
              className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs mb-1 ${
                incident.deleted_at
                  ? 'text-red-600 dark:text-red-400 font-semibold'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <span className="font-mono shrink-0">{formatTicketNumber(incident.ticket_number)}</span>
              <span className="font-sans font-normal">
                Criado por{' '}
                <span className="text-gray-700 dark:text-gray-300">{registrarDisplayName ?? '—'}</span>
                {createdAtLabel != null && (
                  <>
                    {' · '}
                    <span className="tabular-nums">{createdAtLabel}</span>
                  </>
                )}
              </span>
            </div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white leading-snug">{incident.title}</h2>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                TICKET_KIND_BADGE_COLORS[incident.kind] ?? TICKET_KIND_BADGE_COLORS.incident
              }`}>
                {TICKET_KIND_LABELS[incident.kind] ?? incident.kind}
              </span>
              {incident.severity && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_COLORS[incident.severity]}`}>
                  {SEVERITY_LABELS[incident.severity]}
                </span>
              )}
              {stage && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {stage.name}
                </span>
              )}
              {incident.occurred_at && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Ocorrência: {formatDateOnlyPtBr(incident.occurred_at)}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none mt-0.5"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          <section className="space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                {descriptionLabelForKind(incident.kind)}
              </h3>
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">
                {incident.description?.trim() || <span className="italic text-gray-400">—</span>}
              </p>
            </div>
            <div>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                {justificationLabelForKind(incident.kind)}
              </h3>
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">
                {incident.justification?.trim() || <span className="italic text-gray-400">—</span>}
              </p>
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          {isQualityDeptUser && (
            <>
          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Prazo máximo</h3>
            {maxDeadlineInfo.configured ? (
              <div className="flex flex-wrap gap-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Prazo corrido</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{maxDeadlineInfo.label}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Data limite</p>
                  <p className={`text-sm font-medium tabular-nums ${maxDeadlineInfo.breached ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                    {maxDeadlineInfo.deadlineLabel}
                    {maxDeadlineInfo.breached && <span className="ml-1 text-xs font-semibold">(vencido)</span>}
                  </p>
                </div>
                <div className="w-full sm:w-auto sm:min-w-[200px]">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Observações</p>
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                    {maxDeadlineInfo.notes || '—'}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">—</p>
            )}
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />
            </>
          )}

          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">SLA para Primeira Resposta</h3>
            <div className="flex flex-wrap gap-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Tempo Abertura (Horas Úteis)</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">{businessHoursLabel}</p>
              </div>
              {slaDeadlineLabel && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Prazo máximo</p>
                  <p className={`text-sm font-medium tabular-nums ${slaBreached ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                    {slaDeadlineLabel}
                    {slaBreached && <span className="ml-1 text-xs font-semibold">(vencido)</span>}
                  </p>
                </div>
              )}
              {firstResponseLabel ? (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">1ª Resposta em</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white tabular-nums">{firstResponseLabel}</p>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">1ª Resposta em</p>
                  <p className="text-sm font-medium text-gray-400 italic">Sem resposta</p>
                </div>
              )}
            </div>
          </section>

          <hr className="border-gray-200 dark:border-gray-700" />

          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Detalhes</h3>
            <dl className="space-y-2">
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Solicitante</dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {formatRequesterDisplay(incident.requester, activeProfiles, departmentLabelMap, incident.requester_user_id)}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Categoria</dt>
                <dd className="text-sm text-gray-900 dark:text-white">{incident.category?.display_name || '—'}</dd>
              </div>
              {incident.kind === 'not_applicable' ? (
                <div className="flex gap-2">
                  <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Registo improcedente</dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {incident.improcedent_reason?.display_name || '—'}
                  </dd>
                </div>
              ) : (
                <>
                  <div className="flex gap-2">
                    <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Onde</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{incident.origin?.display_name || '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Motivo</dt>
                    <dd className="text-sm text-gray-900 dark:text-white">{incident.ticket_type?.display_name || '—'}</dd>
                  </div>
                </>
              )}
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">{DEPARTMENT_RESP_LABEL}</dt>
                <dd className="text-sm text-gray-900 dark:text-white">{incident.department?.display_name || '—'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Corretor</dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {incident.broker ? formatBrokerTicketLabel(incident.broker) : '—'}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Apólice</dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {incident.policy_number != null ? String(incident.policy_number) : '—'}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Subgrupo</dt>
                <dd className="text-sm text-gray-900 dark:text-white">
                  {incident.convenio_code != null ? String(incident.convenio_code) : '—'}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Link externo</dt>
                <dd className="text-sm text-gray-900 dark:text-white min-w-0">
                  {incident.external_link ? (
                    <a
                      href={incident.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline break-all"
                    >
                      {incident.external_link}
                    </a>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              {incident.closed_at && (
                <div className="flex gap-2">
                  <dt className="text-sm text-gray-500 dark:text-gray-400 w-28 shrink-0">Encerrado em</dt>
                  <dd className="text-sm text-gray-900 dark:text-white">
                    {new Date(incident.closed_at).toLocaleDateString('pt-BR')}
                  </dd>
                </div>
              )}
            </dl>
          </section>

          {stage?.is_closed && (
            <section>
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Resolução</h3>
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-line">
                {incident.resolution || <span className="italic text-gray-400">Nenhuma resolução registrada.</span>}
              </p>
            </section>
          )}

          {/* Seção de anexos */}
          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Anexos {!attachmentsLoading && attachments.length > 0 && `(${attachments.length})`}
            </h3>
            {attachmentsLoading ? (
              <div className="flex justify-center py-3"><LoadingSpinner /></div>
            ) : attachments.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Nenhum anexo.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {attachments.map(att => {
                  const url = attachmentUrls[att.id]
                  const isImage = isTicketAttachmentImage(att)
                  if (isImage) {
                    const imageIdx = imageAttachments.findIndex(a => a.id === att.id)
                    return (
                      <button
                        key={att.id}
                        type="button"
                        onClick={() => setLightboxIndex(imageIdx)}
                        className="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 hover:ring-2 hover:ring-primary-500 transition-all focus:outline-none"
                        title={att.file_name}
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={att.file_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                            {att.file_name}
                          </div>
                        )}
                      </button>
                    )
                  }
                  return (
                    <a
                      key={att.id}
                      href={url || '#'}
                      download={att.file_name}
                      onClick={e => { if (!url) e.preventDefault() }}
                      className="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:ring-2 hover:ring-primary-500 transition-all flex flex-col items-center justify-center gap-1 p-2 text-center no-underline"
                      title={`Baixar ${att.file_name}`}
                    >
                      {isTicketAttachmentPdf(att) ? (
                        <DocumentTextIcon className="h-8 w-8 text-primary-500 dark:text-primary-400 shrink-0" />
                      ) : (
                        <EnvelopeIcon className="h-8 w-8 text-primary-500 dark:text-primary-400 shrink-0" />
                      )}
                      <span className="text-[10px] text-gray-700 dark:text-gray-200 line-clamp-2 break-all">{att.file_name}</span>
                      {att.file_size != null && (
                        <span className="text-[9px] text-gray-400">{formatTicketAttachmentSize(att.file_size)}</span>
                      )}
                      <span className="inline-flex items-center gap-0.5 text-[9px] text-primary-600 dark:text-primary-400 mt-0.5">
                        <ArrowDownTrayIcon className="h-3 w-3" />
                        Baixar
                      </span>
                    </a>
                  )
                })}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Comentários {!commentsLoading && comments.length > 0 && `(${comments.length})`}
            </h3>

            {commentsLoading ? (
              <div className="flex justify-center py-4"><LoadingSpinner /></div>
            ) : comments.length === 0 ? (
              <p className="text-sm text-gray-400 italic mb-3">Nenhum comentário ainda.</p>
            ) : (
              <ul className="space-y-3 mb-4">
                {comments.map(c => (
                  <li key={c.id} className="flex gap-3 group">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-xs font-semibold text-primary-700 dark:text-primary-300 uppercase">
                      {(c.author?.name || 'U').charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                          {c.author?.name || 'Usuário'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(c.created_at).toLocaleString('pt-BR')}
                        </span>
                        {c.user_id === currentUserId && (
                          <button
                            type="button"
                            onClick={() => removeComment(c.id)}
                            className="ml-auto text-xs text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Excluir comentário"
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-gray-900 dark:text-white mt-0.5 whitespace-pre-line">{c.content}</p>
                      {(c.attachments?.length ?? 0) > 0 && (
                        <div className="grid grid-cols-3 gap-2 mt-2">
                          {c.attachments!.map(att => {
                            const url = attachmentUrls[att.id]
                            const isImage = isTicketAttachmentImage(att)
                            if (isImage) {
                              return (
                                <button
                                  key={att.id}
                                  type="button"
                                  onClick={() => openLightboxForAttachment(att)}
                                  className="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 hover:ring-2 hover:ring-primary-500 transition-all focus:outline-none"
                                  title={att.file_name}
                                >
                                  {url ? (
                                    <img
                                      src={url}
                                      alt={att.file_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                      {att.file_name}
                                    </div>
                                  )}
                                </button>
                              )
                            }
                            return (
                              <a
                                key={att.id}
                                href={url || '#'}
                                download={att.file_name}
                                onClick={e => { if (!url) e.preventDefault() }}
                                className="aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:ring-2 hover:ring-primary-500 transition-all flex flex-col items-center justify-center gap-1 p-2 text-center no-underline"
                                title={`Baixar ${att.file_name}`}
                              >
                                {isTicketAttachmentPdf(att) ? (
                                  <DocumentTextIcon className="h-6 w-6 text-primary-500 dark:text-primary-400 shrink-0" />
                                ) : (
                                  <EnvelopeIcon className="h-6 w-6 text-primary-500 dark:text-primary-400 shrink-0" />
                                )}
                                <span className="text-[10px] text-gray-700 dark:text-gray-200 line-clamp-2 break-all">{att.file_name}</span>
                                {att.file_size != null && (
                                  <span className="text-[9px] text-gray-400">{formatTicketAttachmentSize(att.file_size)}</span>
                                )}
                              </a>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-col gap-2">
              <textarea
                ref={textareaRef}
                rows={3}
                placeholder="Escreva um comentário..."
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submitComment()
                }}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
              />
              <input
                ref={commentFileInputRef}
                type="file"
                accept={TICKET_ATTACHMENT_ACCEPT}
                multiple
                className="hidden"
                onChange={handleCommentFileChange}
              />
              {commentAttachmentError && (
                <p className="text-xs text-red-600 dark:text-red-400">{commentAttachmentError}</p>
              )}
              {pendingCommentFiles.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {pendingCommentFiles.map((file, idx) => {
                    const preview = pendingCommentPreviews[idx]
                    const isImage = preview != null
                    const isPdf = isTicketAttachmentPdf({ file_name: file.name, mime_type: file.type })
                    return (
                      <div
                        key={idx}
                        className="relative group aspect-square rounded-lg overflow-hidden border-2 border-dashed border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                      >
                        {isImage ? (
                          <img
                            src={preview}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-1 p-2 text-center">
                            {isPdf ? (
                              <DocumentTextIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 shrink-0" />
                            ) : (
                              <EnvelopeIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 shrink-0" />
                            )}
                            <p className="text-[10px] text-gray-700 dark:text-gray-200 line-clamp-2 break-all">{file.name}</p>
                            <p className="text-[9px] text-gray-500">{formatTicketAttachmentSize(file.size)}</p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removePendingCommentFile(idx)}
                          className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Remover anexo"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                        {isImage && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <p className="text-white text-[10px] truncate">{file.name}</p>
                          </div>
                        )}
                        <span className="absolute top-1 left-1 bg-primary-500 text-white text-[9px] font-bold px-1 rounded">NOVO</span>
                      </div>
                    )
                  })}
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => commentFileInputRef.current?.click()}
                  className="flex items-center gap-1.5 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
                >
                  <PaperClipIcon className="h-4 w-4" />
                  Adicionar arquivo
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 hidden sm:inline">Ctrl+Enter para enviar</span>
                  <Button variant="solid" size="sm" onClick={submitComment} disabled={submitting || !newComment.trim()}>
                    {submitting ? 'Enviando...' : 'Comentar'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Imagens, PDF ou e-mails (.msg, .eml). Máximo {TICKET_ATTACHMENT_MAX_MB} MB por arquivo.
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Histórico de alterações</h3>
            {historyLoading ? (
              <div className="flex justify-center py-4"><LoadingSpinner /></div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Nenhuma alteração registrada.</p>
            ) : (
              <ul className="space-y-2">
                {history.map(h => (
                  <li key={h.id} className="text-sm border-l-2 border-gray-200 dark:border-gray-700 pl-3">
                    <div className="text-gray-500 dark:text-gray-400 text-xs">
                      {new Date(h.changed_at).toLocaleString('pt-BR')}
                      {h.changer?.name && <> · {h.changer.name}</>}
                    </div>
                    <div className="text-gray-900 dark:text-white">
                      <span className="font-medium">{FIELD_LABELS[h.field_changed] || h.field_changed}</span>:{' '}
                      <span className="text-red-500 dark:text-red-400 line-through">
                        {formatHistoryValue(h.field_changed, h.old_value, historyCtx)}
                      </span>
                      {' → '}
                      <span className="text-green-600 dark:text-green-400">
                        {formatHistoryValue(h.field_changed, h.new_value, historyCtx)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {onDelete && canDeleteTicket && !incident.deleted_at && (
              <Button
                variant="outline"
                className="!text-red-600 hover:!bg-red-50 dark:!text-red-400 dark:hover:!bg-red-950/40 !ring-red-200 dark:!ring-red-900/60"
                onClick={() => { void onDelete(incident.id) }}
              >
                Excluir
              </Button>
            )}
            {canEditTicket && !incident.deleted_at && (
              <Button variant="solid" onClick={() => onEdit(incident)}>Editar</Button>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>

      {/* Lightbox (imagens) */}
      {lightboxIndex !== null && imageAttachments[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85"
          onClick={() => setLightboxIndex(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            <img
              src={attachmentUrls[imageAttachments[lightboxIndex].id]}
              alt={imageAttachments[lightboxIndex].file_name}
              className="max-w-full max-h-[80vh] mx-auto rounded-lg shadow-2xl object-contain"
            />
            <p className="text-center text-white text-sm mt-2 opacity-75">
              {imageAttachments[lightboxIndex].file_name}
              {imageAttachments.length > 1 && ` (${lightboxIndex + 1} / ${imageAttachments.length})`}
            </p>

            {/* Fechar */}
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              className="absolute -top-3 -right-3 bg-white dark:bg-gray-800 rounded-full p-1.5 text-gray-700 dark:text-gray-200 hover:bg-gray-100 shadow-lg"
              title="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            {/* Anterior */}
            {lightboxIndex > 0 && (
              <button
                type="button"
                onClick={() => setLightboxIndex(lightboxIndex - 1)}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-10 bg-white dark:bg-gray-800 rounded-full p-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 shadow-lg"
                title="Anterior"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
            )}

            {/* Próximo */}
            {lightboxIndex < imageAttachments.length - 1 && (
              <button
                type="button"
                onClick={() => setLightboxIndex(lightboxIndex + 1)}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-10 bg-white dark:bg-gray-800 rounded-full p-2 text-gray-700 dark:text-gray-200 hover:bg-gray-100 shadow-lg"
                title="Próximo"
              >
                <ArrowRightIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

/** @deprecated Use TicketDetailDrawer */
export const IncidentDetailDrawer = TicketDetailDrawer
